import React, { useState, useRef, useCallback } from 'react';
import _ from 'lodash';
import { makeStyles, Button, Alert, Spinner } from 'design-system';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { handleApiFetchError } from '@scale/frontend-lib/api-fetch';
import ExampleClassification from './components/exampleClassification';
import { useProjectInstructionContext } from '../projectInstructionContext';
import {
  ProjectInstructionQuizType,
  SubmissionProps,
} from '@scale/shared/projectInstruction/types';
import API from '@scale/frontend-lib/dashboard-api';

const QuizPage = () => {
  const {
    title,
    handleStart,
    startButtonText,
    isCorpUser,
    setOpenQuiz,
    quiz,
    setQuiz,
    timeSpentOnQuiz,
    isMobileView,
  } = useProjectInstructionContext();

  const classes = useStyles();
  const [pass, setPass] = useState<boolean | null>(quiz?.isFinished ? true : null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const containerSection = useRef<null | HTMLDivElement>(null);
  const disabled = !isCorpUser;

  const handleUpdate = useCallback(
    _.debounce(
      async () => {
        if (quiz) {
          quiz.timeSpentSec += timeSpentOnQuiz;
          await API.selfserve.updateQuiz(quiz);
        }
      },
      1000,
      {
        leading: true,
        trailing: true,
      },
    ),
    [quiz],
  );

  if (!quiz) {
    return null;
  }
  const submission = quiz?.submission;
  const handleRetry = async () => {
    setPass(null);
    setIsLoading(true);
    const nextQuiz = await API.selfserve.getNextQuiz(quiz.taxonomyVersion);

    if (nextQuiz?.quiz) {
      setQuiz(nextQuiz.quiz);
    } else {
      setQuiz(undefined);
    }
    scrollToTop();
    setIsLoading(false);
  };
  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      quiz.timeSpentSec += timeSpentOnQuiz;
      const res = await API.selfserve.submitQuiz(quiz);
      const { quiz: returnedQuiz } = res;
      setQuiz(returnedQuiz);
      setPass(returnedQuiz.isFinished);
      scrollToTop();
    } catch (err) {
      handleApiFetchError(err);
    }
    setIsLoading(false);
  };

  const scrollToTop = () => {
    if (containerSection.current !== null) {
      containerSection.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const startTaskingButton = (
    <Button
      type="primary"
      disabled={disabled}
      onClick={
        disabled
          ? () => null
          : () => {
              handleStart();
              setPass(null);
            }
      }
    >
      {startButtonText}
    </Button>
  );

  const TryAgainButton = (
    <Button type="error" onClick={handleRetry}>
      Try Again
    </Button>
  );

  if (isMobileView) {
    return (
      <div
        style={{
          padding: 20,
          margin: '0 auto',
          width: 'fit-content',
          background: 'linear-gradient(180deg, #ECF8EB 300px, #F5F5F5 0%)',
        }}
      >
        <h2 className={classes.header}>
          Concept Review [{title}]{' '}
          <Button variant="outlined" type="secondary" onClick={() => setOpenQuiz(false)}>
            View Instructions
          </Button>
        </h2>
        <br />
        <h1>
          Sorry we don't support the concept view on small screen. Please switch to your desktop to
          complete the quiz
        </h1>
        <img
          style={{ position: 'absolute', bottom: 0, left: 0, transform: 'translateX(25%)' }}
          src={`/static/images/unavailable/group.svg`}
          width="400px"
        />
      </div>
    );
  }

  return (
    <div className={classes.background}>
      {isLoading && (
        <div className={classes.loader}>
          <Spinner center size="md" />
        </div>
      )}
      <div className={classes.containerScalers} ref={containerSection}>
        <h2 className={classes.header}>
          Concept Review [{title}]{' '}
          <Button variant="outlined" type="secondary" onClick={() => setOpenQuiz(false)}>
            View Instructions
          </Button>
        </h2>
        <div>
          Now that you’ve read the instructions, it’s time to review your knowledge. Please complete
          the following questions correctly to start tasking. If you’re unsure about any of these
          concepts, remember that you can always refer to the instructions in another tab to ensure
          that you get the right answers.
        </div>
        <br />
        {submission?.length ? (
          <>
            {typeof pass === 'boolean' && (
              <Alert
                severity={pass ? 'success' : 'error'}
                action={pass ? startTaskingButton : TryAgainButton}
              >
                {pass ? (
                  <div>Congratulations you reached 100%! </div>
                ) : (
                  <div>
                    You did not receive a 100% score on your submission. Please retry.{' '}
                    <strong>Try Again</strong> on the bottom of the page
                  </div>
                )}
              </Alert>
            )}

            <div className={classes.quizContainer}>
              {pass !== null && <div className={classes.overlay}></div>}
              {submission.map((node: SubmissionProps, index: number) => {
                switch (node.type) {
                  case ProjectInstructionQuizType.ScenarioLabeling:
                    return null;
                  case ProjectInstructionQuizType.ExampleClassification:
                    return (
                      <ExampleClassification
                        updateQuiz={response => {
                          const copiedSubmission = [...submission];
                          copiedSubmission[index].response = response;
                          copiedSubmission[index].isCorrect = undefined;
                          setQuiz({ ...quiz, submission: copiedSubmission });
                          handleUpdate();
                        }}
                        {...node}
                        index={index + 1}
                      />
                    );
                  default:
                    return null;
                }
              })}
            </div>
            <div className={classes.buttonWrapper}>
              {typeof pass === 'boolean' ? (
                !quiz.isFinished && (
                  <Button type="primary" onClick={handleRetry}>
                    Try Again <FontAwesomeIcon icon="redo" />
                  </Button>
                )
              ) : (
                <Button type="primary" onClick={handleSubmit}>
                  Submit Answers <FontAwesomeIcon icon="arrow-up" />
                </Button>
              )}
            </div>
          </>
        ) : (
          <div className={classes.emptyQuiz}>
            <h3>
              Sorry we don't have quiz for this instruction right now, due to no examples available
            </h3>
            <h3>please start tasking directly {startTaskingButton}</h3>
          </div>
        )}
      </div>
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  background: {
    position: 'relative',
    padding: '30px 20px',
    background: 'linear-gradient(180deg, #ECF8EB 300px, #F5F5F5 0%)',
    color: theme.colors.gray75,
    height: 'fit-content',
    paddingBottom: 100,
    '& h3': {
      fontWeight: 500,
      margin: '0 0 5px 0',
    },
  },
  containerScalers: {
    maxWidth: '1080px',
    margin: 'auto',
    position: 'relative',
    [theme.breakpoints.down('md')]: {
      maxWidth: '95vw',
      padding: '0 16px',
    },
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  buttonWrapper: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 50,
    '& svg': {
      marginLeft: 10,
    },
  },

  quizContainer: {
    position: 'relative',
  },

  overlay: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    background: 'white',
    opacity: 0.1,
  },

  emptyQuiz: {
    margin: 'auto 0',
    textAlign: 'center',
  },

  loader: {
    position: 'fixed',
    left: 0,
    top: 0,
    width: '100%',
    height: '100%',
    zIndex: 9999,
    background: 'rgb(249,249,249,0.3)',
  },
}));

export default QuizPage;
